"use client";

import { useState, useEffect, useRef } from "react";
import { TechnicalAnalysis } from "react-ts-tradingview-widgets";
import "./index.css";

const BASE    = process.env.REACT_APP_URL || "http://127.0.0.1:8000";
const API_KEY = process.env.REACT_APP_API_KEY;

const apiHeaders = () => ({
  "Content-Type": "application/json",
  ...(API_KEY ? { "x-api-key": API_KEY } : {}),
  "ngrok-skip-browser-warning": "true",
});

async function fetchAllSymbols() {
  const res = await fetch(`${BASE}/api/symbols/`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Failed to fetch symbols");
  const data = await res.json();
  return Array.isArray(data) ? data : data.results || [];
}

const Technical = () => {
  const [symbols, setSymbols]           = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [query, setQuery]               = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingSymbols, setLoadingSymbols] = useState(true);
  const dropdownRef = useRef();

  useEffect(() => {
    fetchAllSymbols()
      .then(list => {
        setSymbols(list);
        if (list.length > 0) setSelectedSymbol(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoadingSymbols(false));
  }, []);

  // close dropdown on outside click
  useEffect(() => {
    function handle(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = query.trim()
    ? symbols.filter(s =>
        s.code?.toLowerCase().includes(query.toLowerCase()) ||
        s.name?.toLowerCase().includes(query.toLowerCase())
      )
    : symbols;

  const pickSymbol = (sym) => {
    setSelectedSymbol(sym);
    setQuery("");
    setShowDropdown(false);
  };

  return (
    <div className="main-container">
      <div className="control-bar">
        <div className="title-area">
          <h1>Technical Analysis</h1>
        </div>

        <div className="symbol-control">
          <p>Symbol:</p>

          {/* selected badge */}
          {selectedSymbol && (
            <span style={{
              background: "#f98300", color: "#fff",
              padding: "4px 12px", borderRadius: 6,
              fontFamily: "monospace", fontWeight: 700, fontSize: 14,
            }}>
              {selectedSymbol.code}
            </span>
          )}

          {/* searchable dropdown */}
          <div ref={dropdownRef} style={{ position: "relative" }}>
            <div style={{
              display: "flex", alignItems: "center",
              border: "1px solid #e5e7eb", borderRadius: 8,
              overflow: "hidden", background: "#fff",
            }}>
              <span style={{ padding: "0 8px", color: "#bbb", fontSize: 15 }}>🔍</span>
              <input
                type="text"
                placeholder={loadingSymbols ? "Loading…" : "Search symbol…"}
                value={query}
                disabled={loadingSymbols}
                onChange={e => { setQuery(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                style={{
                  border: "none", outline: "none",
                  padding: "7px 10px 7px 0", fontSize: 13,
                  width: 170, fontFamily: "inherit", background: "transparent",
                }}
              />
            </div>

            {showDropdown && filtered.length > 0 && (
              <div style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0,
                background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                maxHeight: 260, overflowY: "auto", zIndex: 999, minWidth: 220,
              }}>
                {filtered.slice(0, 80).map(sym => (
                  <div
                    key={sym.code}
                    onMouseDown={() => pickSymbol(sym)}
                    style={{
                      padding: "8px 14px", cursor: "pointer", fontSize: 13,
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      borderBottom: "1px solid #f5f5f5",
                      background: selectedSymbol?.code === sym.code ? "#fff7ed" : "transparent",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                    onMouseLeave={e => e.currentTarget.style.background = selectedSymbol?.code === sym.code ? "#fff7ed" : "transparent"}
                  >
                    <span style={{ fontWeight: 700, color: "#050a30", fontFamily: "monospace" }}>{sym.code}</span>
                    <span style={{ color: "#888", fontSize: 11, marginLeft: 8, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {sym.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="chart-area">
        {selectedSymbol ? (
          <TechnicalAnalysis
            key={selectedSymbol.code}
            symbol={`PSX:${selectedSymbol.code}`}
            colorTheme="light"
            width="100%"
            height={600}
            displayMode="multiple"
            showIntervalTabs={true}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#bbb" }}>
            {loadingSymbols ? "Loading symbols…" : "No symbol selected"}
          </div>
        )}
      </div>
    </div>
  );
};

export default Technical;
